import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser, verificationUser, verifyLogin } from "../model/LoginModel";
import Logger from "../../base/Logger";
import UserUtils from "../UserUtils";
import { useToast } from "../../components/provider/ToastProvider";

const { handleLoggedIn, getUserName } = UserUtils;

// AUTO-LOGIN EXECUTION - This runs immediately when module loads
const storedEmail = sessionStorage.getItem("email");
const currentUrl = window.location.href;
const loginParam = new URL(currentUrl).searchParams.get("login");

console.log("LoginVM Module Loading:", { 
  storedEmail, 
  loginParam, 
  currentUrl,
  userName: getUserName(),
  existingToken: localStorage.getItem("token")
});

if (loginParam === "true") {
  console.log("Auto-login detected, starting process...");
  const userName = getUserName();
  console.log("Current userName:", userName);
  
  if (userName === null || userName === "") {
    if (storedEmail) {
      console.log("Executing auto-login for email:", storedEmail);
      
      // Execute auto-login
      const resultPromise = loginUser(storedEmail);
      resultPromise
        .then((result) => {
          console.log("Auto-login API success, result:", result);
          
          // Call handleLoggedIn to populate localStorage
          console.log("Calling handleLoggedIn...");
          handleLoggedIn(result);
          
          // Check localStorage immediately after
          console.log("LocalStorage after handleLoggedIn:", {
            token: localStorage.getItem("token"),
            userName: localStorage.getItem("userName"),
            permissionData: localStorage.getItem("permissionData"),
            customerName: localStorage.getItem("customerName"),
            terminal: localStorage.getItem("terminal"),
            terminal_name: localStorage.getItem("terminal_name"),
            time_zone: localStorage.getItem("time_zone")
          });

          // Dispatch custom storage event
          console.log("Dispatching cStorage event...");
          const event = new CustomEvent("cStorage", {});
          window.dispatchEvent(event);
          
          console.log("Auto-login process completed successfully");
        })
        .catch((error) => {
          console.error("Auto-login failed:", error);
          Logger.e("Auto-login failed:", error);
          
          // Clean up on failure
          sessionStorage.removeItem("email");
          localStorage.removeItem("token");
          localStorage.removeItem("userName");
          localStorage.removeItem("permissionData");
        });
    } else {
      console.log("No stored email found for auto-login");
    }
  } else {
    console.log("User already logged in, verifying session...");
    
    // User already has data, just verify
    verifyLogin()
      .then(() => {
        console.log("Session verification successful");
        
        // Check existing localStorage
        console.log("Existing localStorage:", {
          token: localStorage.getItem("token"),
          userName: localStorage.getItem("userName"),
          permissionData: localStorage.getItem("permissionData")
        });
        
        // Still dispatch event to trigger UI updates
        const event = new CustomEvent("cStorage", {});
        window.dispatchEvent(event);
      })
      .catch((error) => {
        console.error("Session verification failed:", error);
        Logger.e("Something went wrong in verification");
        
        // Clear invalid data
        localStorage.removeItem("token");
        localStorage.removeItem("userName");
        localStorage.removeItem("permissionData");
      });
  }
}

const LoginVM = () => {
  const { handleToastOpen } = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Move verifyAndRedirect inside useEffect to prevent infinite loop
  useEffect(() => {
    const verifyAndRedirect = async () => {
      const userName = getUserName();
      console.log("Manual verifyAndRedirect called:", { userName });
      
      if (userName !== null && userName !== "") {
        try {
          console.log("Verifying existing session...");
          setLoading(true);
          await verifyLogin();
          setLoading(false);
          console.log("Session verified, redirecting to /services");
          navigate("/services");
        } catch (error) {
          console.error("Session verification failed:", error);
          setLoading(false);
          Logger.e("Something went wrong in verification");
          
          // Clear invalid session data
          localStorage.removeItem("token");
          localStorage.removeItem("userName");
          localStorage.removeItem("permissionData");
        }
      } else {
        console.log("No existing session found");
        setLoading(false);
      }
    };
    
    // Only run verification if not in auto-login mode
    if (loginParam !== "true") {
      verifyAndRedirect();
    }
  }, []); // Empty dependency array to run only once

  const handleLogin = async () => {
    console.log("Manual login started for email:", email);
    setLoading(true);

    try {
      const REACT_APP_BE_URL = process.env.REACT_APP_BE_URL;
      console.log("Backend URL:", REACT_APP_BE_URL);

      const msAuthClick = async (email) => {
        try {
          const redirectUrl = `${REACT_APP_BE_URL}/users/auth/microsoft?feURL=${encodeURIComponent(
            currentUrl
          )}&email=${encodeURIComponent(email)}`;
          
          console.log("Redirecting to Microsoft auth:", redirectUrl);
          console.log("Storing email in sessionStorage:", email);
          
          // Clear any existing data to ensure clean state
          localStorage.removeItem("token");
          localStorage.removeItem("userName");
          localStorage.removeItem("permissionData");
          
          window.location.href = redirectUrl;
        } catch (error) {
          console.error("Microsoft auth redirect failed:", error);
          Logger.e("Something went wrong in authentication");
          setLoading(false);
        }
      };

      // Verify user exists first
      console.log("Verifying user exists...");
      await verificationUser(email);
      console.log("User verification successful");
      
      // Store email before redirect
      sessionStorage.setItem("email", email);
      console.log("Email stored in sessionStorage");
      
      // Proceed with Microsoft auth
      await msAuthClick(email);
    } catch (error) {
      console.error("Login process failed:", error);
      handleToastOpen("error", error.message);
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    handleLogin,
    loading,
  };
};

export default LoginVM;